import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MenuChatsContext from 'contexts/menuChats/context';
import ConversationContext from './context';

export type TemplateSelected = {
  dateAdded: object;
  preview: object;
  sharedWith: object;
  template: string;
  _id: string;
};

export type ContactSelected = {
  _id: string;
  name: string;
  phone: string;
  lastChat?: string;
  tagsList: string;
};

export interface IConversation {
  openStartNewConversation: boolean;
  handleStartNewConversation: (value: boolean) => void;
  contactSelected?: ContactSelected;
  handleSelectContact: (contact?: ContactSelected) => void;
  templateSelected?: TemplateSelected;
  handleSelectTemplate: (template?: TemplateSelected) => void;
}

const ConversationProvider: React.FC<React.PropsWithChildren> = (props) => {
  const { children } = props;
  const navigate = useNavigate();
  const { setOpenContactManager, setOpenChatsManager, setOpenServiceDashboard } = useContext(MenuChatsContext);
  const [openStartNewConversation, setOpenStartNewConversation] = useState(false);
  const [contactSelected, setContactSelected] = useState<ContactSelected>();
  const [templateSelected, setTemplateSelected] = useState<TemplateSelected>();

  // Tracks whether the panel should reopen after being closed for a reset.
  // Used when handleStartNewConversation(true) is called while the panel is
  // already open — e.g. the user clicks "Start new conversation" after being
  // redirected to the message history screen following a successful send.
  const shouldReopenRef = useRef(false);

  const handleStartNewConversation = (value: boolean) => {
    if (value) {
      setOpenContactManager(false);
      setOpenChatsManager(false);
      setOpenServiceDashboard(false);

      if (openStartNewConversation) {
        // The panel is already open. Closing it unmounts <Conversation />,
        // which resets all its local state (e.g. showHistoryData, inputText).
        // The useEffect below detects this and immediately reopens the panel.
        shouldReopenRef.current = true;
        setOpenStartNewConversation(false);
        return;
      }
    }
    setOpenStartNewConversation(value);
  };

  const handleSelectContact = (contact?: ContactSelected) => {
    setContactSelected(contact);
  };

  const handleSelectTemplate = (template?: TemplateSelected) => {
    setTemplateSelected(template);
  };

  useEffect(() => {
    if (openStartNewConversation) {
      navigate('/', { replace: true });
    } else {
      setContactSelected(undefined);
      setTemplateSelected(undefined);

      if (shouldReopenRef.current) {
        shouldReopenRef.current = false;
        setOpenStartNewConversation(true);
      }
    }
  }, [openStartNewConversation]);

  const memoizedValues = useMemo(
    () => ({
      openStartNewConversation,
      handleStartNewConversation,
      contactSelected,
      handleSelectContact,
      templateSelected,
      handleSelectTemplate,
    }),
    [openStartNewConversation, contactSelected, templateSelected]
  );

  return <ConversationContext.Provider value={memoizedValues}>{children}</ConversationContext.Provider>;
};

export default ConversationProvider;
